import type { BaseIconProps, Size } from "@/assets/icons/utils";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { type VariantProps, cva } from "class-variance-authority";
import { type FC, type ReactElement, type ReactNode } from "react";

const pillVariants = cva("inline-flex items-center gap-1 rounded-[20px] border-0 px-2 py-0.5 font-normal", {
    variants: {
        variant: {
            success: "bg-surface-success-subtle text-content-success dark:text-content-success-subtle",
            warning:
                "bg-surface-warning-subtle dark:bg-[rgb(var(--ona-orange-400)/0.25)] text-content-warning dark:text-content-warning-subtle",
            danger: "bg-surface-destructive-subtle text-content-destructive dark:text-content-destructive-subtle",

            // In the Ona theme these are all the same
            default: "bg-surface-muted text-content-strong",
            info: "bg-surface-muted text-content-strong",
            neutral: "bg-surface-muted text-content-strong",

            brand: "bg-surface-brand text-content-brand dark:text-content-brand-subtle",
        },
        size: {
            sm: "px-1.5 py-0.5 text-xs",
            md: "px-2 py-1 text-sm",
            lg: "px-3 py-1.5 text-base",
        },
    },
    defaultVariants: {
        variant: "default",
        size: "md",
    },
});

export type PillVariant = VariantProps<typeof pillVariants>["variant"];

export type PillProps = {
    children: ReactNode;
    className?: string;
    tooltip?: string | ReactElement;
    LeadingIcon?: React.ComponentType<BaseIconProps>;
    TrailingIcon?: React.ComponentType<BaseIconProps>;
    truncate?: boolean;
} & VariantProps<typeof pillVariants>;

export const Pill: FC<PillProps> = ({
    children,
    className,
    variant,
    size,
    tooltip,
    LeadingIcon,
    TrailingIcon,
    truncate,
}) => {
    let iconSize: Size = "base";
    switch (size) {
        case "sm":
            iconSize = "sm";
            break;
        case "md":
            iconSize = "base";
            break;
        case "lg":
            iconSize = "lg";
            break;
    }

    const content = (
        <span className={cn(pillVariants({ variant, size }), className)} data-variant={variant}>
            {LeadingIcon && <LeadingIcon size={iconSize} aria-hidden={true} />}
            <span className={truncate ? "truncate" : ""}>{children}</span>
            {TrailingIcon && <TrailingIcon size={iconSize} aria-hidden={true} />}
        </span>
    );

    if (tooltip) {
        return <Tooltip content={tooltip}>{content}</Tooltip>;
    }

    return content;
};
